import { AxiosError } from 'axios'
import type {
  APIErrorResponse,
  ErrorCategory,
} from '@/types/errors'
import {
  categorizeError,
  isAuthError,
  isValidationError,
  isResourceError,
  isTrainingError,
  isPredictionError,
  isDataSourceError,
  isRateLimitError,
  isPlanLimitError,
  isInternalError,
} from '@/types/errors'

// Re-export error type guards for convenience
export {
  isAuthError,
  isValidationError,
  isResourceError,
  isTrainingError,
  isPredictionError,
  isDataSourceError,
  isRateLimitError,
  isPlanLimitError,
  isInternalError,
}

/**
 * Type guard for Axios errors.
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return error instanceof Error && 'isAxiosError' in error
}

/**
 * Type guard for API error responses.
 * Checks if error has the structure of an APIErrorResponse.
 */
export function isAPIErrorResponse(error: unknown): error is APIErrorResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'error_code' in error &&
    'message' in error &&
    typeof (error as APIErrorResponse).error_code === 'string' &&
    typeof (error as APIErrorResponse).message === 'string'
  )
}

/**
 * Extract APIErrorResponse from any error type.
 * Handles Axios errors, APIErrorResponse objects, and generic errors.
 */
export function extractAPIError(error: unknown): APIErrorResponse | null {
  // Direct APIErrorResponse
  if (isAPIErrorResponse(error)) {
    return error
  }

  // Axios error with APIErrorResponse in response.data
  if (isAxiosError(error) && error.response?.data) {
    if (isAPIErrorResponse(error.response.data)) {
      return error.response.data
    }
  }

  return null
}

/**
 * Get user-friendly error message from any error type.
 * Prioritizes API error messages over generic error messages.
 */
export function getErrorMessage(error: unknown): string {
  // Try to extract structured API error first
  const apiError = extractAPIError(error)
  if (apiError) {
    return apiError.message
  }

  // Axios error (fallback for errors without structured response)
  if (isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined
    return (data?.message as string) || error.message
  }

  // Standard Error
  if (error instanceof Error) {
    return error.message
  }

  // String error
  if (typeof error === 'string') {
    return error
  }

  return 'An unknown error occurred'
}

/**
 * Get API error message (legacy alias for getErrorMessage).
 * @deprecated Use getErrorMessage instead
 */
export function getApiErrorMessage(error: unknown): string {
  return getErrorMessage(error)
}

/**
 * Get error code from any error type.
 * Returns null if error doesn't have a code.
 */
export function getErrorCode(error: unknown): string | null {
  const apiError = extractAPIError(error)
  return apiError?.error_code || null
}

/**
 * Categorize error based on HTTP status code or error type.
 * Useful for determining retry behavior and user messaging.
 */
export function categorizeAPIError(error: unknown): ErrorCategory {
  // Try HTTP status code first (from Axios error)
  if (isAxiosError(error) && error.response?.status) {
    return categorizeError(error.response.status)
  }

  // Fallback: categorize by error code
  const apiError = extractAPIError(error)
  if (apiError) {
    if (isAuthError(apiError)) {
      return { category: 'auth', isRetryable: false, requiresUserAction: true }
    }
    if (isValidationError(apiError)) {
      return { category: 'validation', isRetryable: false, requiresUserAction: true }
    }
    if (isResourceError(apiError)) {
      return { category: 'resource', isRetryable: false, requiresUserAction: true }
    }
    if (isRateLimitError(apiError) || isPlanLimitError(apiError)) {
      return { category: 'rate_limit', isRetryable: true, requiresUserAction: false }
    }
    if (isInternalError(apiError)) {
      return { category: 'server', isRetryable: true, requiresUserAction: false }
    }
  }

  return { category: 'unknown', isRetryable: false, requiresUserAction: true }
}

/**
 * Check if error is retryable based on error type.
 * Rate limit errors and server errors are typically retryable.
 */
export function isRetryableError(error: unknown): boolean {
  return categorizeAPIError(error).isRetryable
}

/**
 * Check if error requires user action to resolve.
 * Auth, validation, and resource errors typically require user intervention.
 */
export function requiresUserAction(error: unknown): boolean {
  return categorizeAPIError(error).requiresUserAction
}

/**
 * Format error for logging/debugging.
 * Includes all available error details.
 */
export function formatErrorForLogging(error: unknown): Record<string, unknown> {
  const apiError = extractAPIError(error)
  const category = categorizeAPIError(error)

  return {
    message: getErrorMessage(error),
    error_code: apiError?.error_code,
    context: apiError?.context,
    category: category.category,
    isRetryable: category.isRetryable,
    requiresUserAction: category.requiresUserAction,
    httpStatus: isAxiosError(error) ? error.response?.status : undefined,
    // Include full error for debugging (in non-production)
    ...(import.meta.env.DEV && { rawError: error }),
  }
}
